import * as React from 'react';
import withStyles from 'react-jss';
import { DropdownMasterValues, Field } from 'src/dto/response/getTaskDetails';
import useFieldGroup from 'src/hooks/form/useFieldGroup';
import { isValidArray } from 'src/Library/Helper';
import { StylesProps } from 'src/Library/Types';
import SEFieldInput from './SEFieldInput';
import SEFieldLabel from './SEFieldLabel';
import { FieldHookProps, FieldNodeProps } from 'src/components/common/form/form.types';
import * as lodash from 'lodash';
import SEMultiFieldGroup from './SEMultiFieldGroup';
import SESubFieldGroup from './SESubFieldGroup';
import SEQuoteTaskTabs from './hooks/SEQuotesComponents/SEQuoteTaskTabs';
import {
  ChaSubFieldTypes,
  CfsOnlyContainerSubFieldTypes,
  CHECKBOX_FIELD_TYPE,
  FF_TYPE_FF_DIRECT_CONTRACT,
  FFSubFieldTypes,
  IcdOnlyContainerSubFieldTypes,
  InterCartingSubFieldTypes,
  isBreakBulkMode,
  isBreakBulkVendorField,
  isCfsContractorField,
  isChaContractorField,
  isFFVendorCheckboxField,
  isFreightForwarderField,
  isIcdContractorField,
  isInterCartingContractorField,
  isPerVendorQuoteOrBidField,
  getQuoteTabOrder,
  isQuoteSelectionTaskFields,
  isShippingLineField,
  isSLVendorCheckboxField,
  QUOTE_DEVIATION_REMARKS_CODE,
  QUOTE_L1_DEVIATION_REASON_CODE,
  isSurveyorField,
  SharedContainerSubFieldTypes,
  SurveyorSubFieldTypes,
} from '../SE.constants';

interface SEFieldGroupProps extends StylesProps<ReturnType<typeof styles>> {
  children?: any;
  fieldGroup: ReturnType<typeof useFieldGroup>;
  dropdownMasterValues: DropdownMasterValues;
  onSubmit?: () => void;
  duplicateField?: (field: any) => void;
  deleteField?: (field: any) => void;
  isMultiFieldPresent?: boolean;
  enableGridView?: boolean;
  shipmentMode?: string;
}

const SEFieldGroup: React.FC<SEFieldGroupProps> = ({
  fieldGroup,
  classes,
  dropdownMasterValues,
  onSubmit,
  duplicateField,
  deleteField,
  isMultiFieldPresent = false,
  enableGridView = false,
  shipmentMode,
}) => {
  /** SMT70 (Vendor Type Selection): live CFS/ICD checkbox state driving conditional field visibility. */
  const [vendorCheckboxState, setVendorCheckboxState] = React.useState<{ [key: string]: boolean }>({});
  /** SMT70: live value of value-driven sub-field dropdowns (e.g. FF Type) that gate other fields. */
  const [subFieldValueState, setSubFieldValueState] = React.useState<{ [key: string]: any }>({});

  const asyncRerender = (cb: () => boolean) => {
    setTimeout(() => {
      if (cb()) {
        fieldGroup.requestRerender();
      } else {
        asyncRerender(cb);
      }
    }, 100);
  };

  const renderSubGroups = (subGroups) => {
    return (
      subGroups.map((subGroup) => {
        return (
          <>
            <SEFieldLabel
              bold
              label={subGroup?.extraData?.name}
              isRequired={subGroup?.extraData?.isMandatory}
            />
            <SESubFieldGroup
              fieldGroup={subGroup}
              dropdownMasterValues={dropdownMasterValues}
              onSubmit={onSubmit}
            />
          </>
        );
      }) || null
    );
  };

  /**
   * Field types that render their own heading and would otherwise show it twice — once from
   * `SEFieldLabel` here and once inside the field. `ADDITIONAL_CHARGES` (ISS-291748) puts its labels in
   * a table header, so the group-level "Additional Charges" above it is pure duplication.
   *
   * Only the `SEFieldLabel` push is suppressed: `showLabel` is otherwise read solely by
   * `SEMultiFieldGroup`, which these single fields never reach.
   */
  const FIELD_TYPES_WITH_OWN_HEADING = ['ADDITIONAL_CHARGES'];

  const getElemsWithShowLabel = ({
    elems,
    isParentObject,
  }: {
    elems: {
      Node: any;
      ref?: {
        current?: {
          field: {
            refs?: {
              current?: {
                field?: {
                  extraData?: {
                    fieldTemplateId: string;
                  };
                };
              };
            }[];
          };
        };
      };
    }[];
    isParentObject: boolean;
  }) => {
    const groupedElems = lodash.groupBy(elems, (elem) => {
      const refs = elem?.ref?.current?.field?.refs;
      return refs ? refs[0]?.current?.field?.extraData?.fieldTemplateId : undefined;
    });
    return Object.keys(groupedElems).reduce((acc, key) => {
      acc = [
        ...acc,
        ...groupedElems[key]?.map((elem, idx) => {
          const isLastElem = groupedElems[key]?.length - 1 === idx;
          const isMoreThanOneElem = groupedElems[key]?.length > 1;
          const field = elem?.ref?.current?.field as any;
          const isFieldObject = field?.defaultRenderProps?.extraData?.type === 'OBJECT';
          const isFieldSingle = field?.fieldType !== 'multi' && field?.fieldType !== 'group';
          const isFieldMulti = field?.fieldType === 'multi' || isMultiFieldPresent;
          /**
           * `defaultRenderProps` is set by `useMultiField` only, so a single field carries its payload
           * directly on `extraData` — read both, or this silently never matches for leaf fields.
           */
          const fieldPayloadType =
            field?.extraData?.type || field?.defaultRenderProps?.extraData?.type;
          const rendersOwnHeading =
            !!fieldPayloadType &&
            FIELD_TYPES_WITH_OWN_HEADING.includes(String(fieldPayloadType).toUpperCase());
          const showLabel =
            !rendersOwnHeading &&
            ((isFieldSingle && !isParentObject) || (isFieldObject && idx === 0) || isFieldMulti);

          return {
            ...elem,
            showLabel,
            labelBold: isFieldObject,
            showDelete: isMoreThanOneElem,
            showAdd: isLastElem,
          };
        }),
      ];
      return acc;
    }, [] as any[]) as any[];
  };

  const renderFieldGroup = () => {
    const isParentObject = fieldGroup?.extraData?.type === 'OBJECT';
    return fieldGroup?.render(elems => {
      const subGroups: any[] = [];
      const res: any[] = [];
      const objectGroupLabels: any[] = [];
      const quoteTabNodes: any[] = [];
      const quoteTabMeta: any[] = [];
      let elemsWithShowLabel: any[] = [];
      if (isValidArray(elems)) {
        elemsWithShowLabel = getElemsWithShowLabel({
          elems: elems as any[],
          isParentObject,
        });

        /**
         * SMT70 — Vendor Type Selection conditional visibility.
         * Auto-scoped: only active when vendor checkboxes are present in this group.
         *  - CFS selected      -> reveal shared sub-fields + Segment, Panel Identifier, CFS Buffer Days
         *  - ICD selected      -> reveal shared sub-fields + ICD Buffer Days
         *  - CHA selected      -> reveal Material Category + Plant Type
         *  - Surveyor selected -> reveal Surveyor Type
         *  - none selected     -> hide that vendor's sub-fields
         *  - CFS <-> ICD are mutually exclusive (the opposite checkbox is disabled + cleared)
         */
        const getElemField = (elem: any) => elem?.ref?.current?.field;
        let cfsField: any;
        let icdField: any;
        let chaField: any;
        let surveyorField: any;
        let interCartingField: any;
        let ffField: any;
        let slField: any;
        let ffTypeField: any;
        elemsWithShowLabel.forEach((elem) => {
          const f = getElemField(elem);
          const fType = f?.extraData?.type;
          if (FFSubFieldTypes.includes(fType)) ffTypeField = f;
          if (fType !== CHECKBOX_FIELD_TYPE) return;
          const name = f?.extraData?.name;
          if (isCfsContractorField(name)) cfsField = f;
          else if (isIcdContractorField(name)) icdField = f;
          else if (isChaContractorField(name)) chaField = f;
          else if (isSurveyorField(name)) surveyorField = f;
          else if (isInterCartingContractorField(name)) interCartingField = f;
          else if (isFFVendorCheckboxField(name)) ffField = f;
          else if (isSLVendorCheckboxField(name)) slField = f;
        });
        const isVendorSelection = !!(cfsField || icdField || chaField || surveyorField || interCartingField || ffField);
        const isVendorChecked = (f: any) =>
          f ? (f.key in vendorCheckboxState ? vendorCheckboxState[f.key] : (f.value === 'true' || f.value === true)) : false;
        const cfsChecked = isVendorChecked(cfsField);
        const icdChecked = isVendorChecked(icdField);
        const chaChecked = isVendorChecked(chaField);
        const surveyorChecked = isVendorChecked(surveyorField);
        const interCartingChecked = isVendorChecked(interCartingField);
        const ffChecked = isVendorChecked(ffField);

        /** FF Type dropdown value (live state first, saved field value as fallback). */
        const ffTypeValue = ffTypeField
          ? (ffTypeField.key in subFieldValueState ? subFieldValueState[ffTypeField.key] : ffTypeField.value)
          : undefined;
        /** SL is disabled while FF is active and its FF Type is "FF Direct Contract". */
        const isFfDirectContract =
          ffChecked &&
          (Array.isArray(ffTypeValue)
            ? ffTypeValue.includes(FF_TYPE_FF_DIRECT_CONTRACT)
            : ffTypeValue === FF_TYPE_FF_DIRECT_CONTRACT);

        const breakBulkMode = isBreakBulkMode(shipmentMode);

        const isHiddenVendorField = (f: any): boolean => {
          if (!isVendorSelection || !f) return false;
          const type = f?.extraData?.type;
          const name = f?.extraData?.name;
          // Mode-driven vendor-type visibility (only when the shipment mode is known)
          if (type === CHECKBOX_FIELD_TYPE && shipmentMode) {
            if (breakBulkMode) {
              if (isFreightForwarderField(name) || isShippingLineField(name)) return true;
            } else if (isBreakBulkVendorField(name)) {
              return true;
            }
          }
          // CFS-only sub-fields (Segment, Panel Identifier, CFS Buffer Days)
          if (CfsOnlyContainerSubFieldTypes.includes(type)) return !cfsChecked;
          // ICD-only sub-fields (ICD Buffer Days)
          if (IcdOnlyContainerSubFieldTypes.includes(type)) return !icdChecked;
          // Shared container sub-fields (Cargo Type, Gauge Status, Destuff Indicator)
          if (SharedContainerSubFieldTypes.includes(type)) return !(cfsChecked || icdChecked);
          // CHA sub-fields (Material Category, Plant Type)
          if (ChaSubFieldTypes.includes(type)) return !chaChecked;
          // Surveyor sub-fields (Surveyor Type)
          if (SurveyorSubFieldTypes.includes(type)) return !surveyorChecked;
          // Inter-Carting sub-fields (Contract Type)
          if (InterCartingSubFieldTypes.includes(type)) return !interCartingChecked;
          // Freight Forwarder sub-fields (FF Type)
          if (FFSubFieldTypes.includes(type)) return !ffChecked;
          return false;
        };

        /** CFS <-> ICD are mutually exclusive: the opposite checkbox stays visible but disabled. */
        const isDisabledVendorCheckbox = (f: any): boolean => {
          if (!isVendorSelection || f?.extraData?.type !== CHECKBOX_FIELD_TYPE) return false;
          const name = f?.extraData?.name;
          if (cfsChecked && isIcdContractorField(name)) return true;
          if (icdChecked && isCfsContractorField(name)) return true;
          // FF Type = "FF Direct Contract" disables the SL (Shipping Line) vendor checkbox.
          if (isFfDirectContract && isSLVendorCheckboxField(name)) return true;
          return false;
        };

        const isVendorCheckbox = (f: any) =>
          isVendorSelection &&
          f?.extraData?.type === CHECKBOX_FIELD_TYPE &&
          (isCfsContractorField(f?.extraData?.name) ||
            isIcdContractorField(f?.extraData?.name) ||
            isChaContractorField(f?.extraData?.name) ||
            isSurveyorField(f?.extraData?.name) ||
            isInterCartingContractorField(f?.extraData?.name) ||
            isFFVendorCheckboxField(f?.extraData?.name) ||
            isSLVendorCheckboxField(f?.extraData?.name));

        const handleVendorCheckboxToggle = (f: any, checked: boolean) => {
          setVendorCheckboxState((prev) => {
            const next = { ...prev, [f.key]: checked };
            if (checked) {
              const name = f?.extraData?.name;
              const opposite = isCfsContractorField(name)
                ? icdField
                : isIcdContractorField(name)
                ? cfsField
                : undefined;
              if (opposite) {
                opposite.setValue('false');
                next[opposite.key] = false;
              }
            }
            return next;
          });
        };

        const renderFieldInputNode = (field: any) => {
          const fieldType = field?.extraData?.type || field?.defaultRenderProps?.extraData?.type;
          const isFullWidthField = FIELD_TYPES_WITH_OWN_HEADING.includes(String(fieldType).toUpperCase());
          return (
            <SEFieldInput
              field={field}
              dropdownMasterValues={dropdownMasterValues}
              style={{ width: isFullWidthField ? '100%' : isParentObject ? '95%' : '90%' }}
              {...(isVendorCheckbox(field)
                ? {
                    onCheckboxToggle: (checked: boolean) => handleVendorCheckboxToggle(field, checked),
                    disabled: isDisabledVendorCheckbox(field),
                  }
                : {})}
              {...(FFSubFieldTypes.includes(field?.extraData?.type)
                ? {
                    onValueChange: (val: any) => {
                      setSubFieldValueState((prev) => ({ ...prev, [field.key]: val }));
                      // FF Type = "FF Direct Contract" removes SL — clear any prior SL selection so a
                      // hidden checkbox never submits a stale checked value.
                      const isDirect = Array.isArray(val)
                        ? val.includes(FF_TYPE_FF_DIRECT_CONTRACT)
                        : val === FF_TYPE_FF_DIRECT_CONTRACT;
                      if (isDirect && slField) {
                        slField.setValue('false');
                        setVendorCheckboxState((prev) => ({ ...prev, [slField.key]: false }));
                      }
                    },
                  }
                : {})}
            />
          );
        };

        if (isParentObject) {
          const groupLabels = elemsWithShowLabel.map((elem) => {
            if (!elem) {
              return;
            }
            const { ref } = elem;
            const field = ref?.current?.field;
            return (
              <SEFieldLabel
                style={{ fontSize: 10, color: '#666666' }}
                label={field?.label}
                isRequired={field?.isRequired}
                onEdit={onSubmit && (() => {
                  field?.enable();
                  asyncRerender(() => !field?.disabled);
                })}
              />
            );
          });
          objectGroupLabels.push(...groupLabels);
        }

        const multiEntryIndices: number[] = [];
        elemsWithShowLabel.forEach((elem, idx) => {
          if (!elem) return;
          const field = elem?.ref?.current?.field;
          const isMultiEntry = field?.fieldType === 'multi' && field?.defaultRenderProps?.extraData?.isMultipleEntryType;
          if (isMultiEntry) {
            multiEntryIndices.push(idx);
          }
        });

        // Find all consecutive pairs of multi-entry indices
        const consecutivePairs: number[][] = [];
        if (multiEntryIndices.length >= 2) {
          for (let i = 0; i < multiEntryIndices.length - 1; i++) {
            if (multiEntryIndices[i + 1] === multiEntryIndices[i] + 1) {
              consecutivePairs.push([multiEntryIndices[i], multiEntryIndices[i + 1]]);
              i++;
            }
          }
        }

        const gridRenderedIndices = new Set<number>();

        if (enableGridView && consecutivePairs.length > 0) {
      
          consecutivePairs.forEach((pair, pairIndex) => {
            const gridElements: any[] = [];

            pair.forEach((idx) => {
              const elem = elemsWithShowLabel[idx];
              if (!elem) return;
              const { Node, ref, nodeKey } = elem;
              const field = ref?.current?.field;

              isMultiFieldPresent = true;
              gridRenderedIndices.add(idx);

              gridElements.push(
                <Node key={nodeKey}>
                  <div>
                    <SEFieldLabel
                      label={field?.defaultRenderProps?.label || field?.label}
                      isRequired={
                        field?.isRequired ||
                        field?.defaultRenderProps?.extraData?.isMandatory
                      }
                      onEdit={onSubmit && (() => {
                        field?.enable();
                        asyncRerender(() => !field?.disabled);
                      })}
                      style={{ marginBottom: 8 }}
                    />
                    <SEMultiFieldGroup
                      fieldGroup={fieldGroup}
                      duplicateField={duplicateField}
                      deleteField={deleteField}
                      onSubmit={onSubmit}
                      dropdownMasterValues={dropdownMasterValues}
                      field={field}
                      showLabel={false}
                      showDelete={elem?.showDelete}
                      showAdd={elem?.showAdd}
                    />
                  </div>
                </Node>
              );
            });

            res.push(
              <div key={`multi-entry-grid-${pairIndex}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                {gridElements}
              </div>
            );
          });


          elemsWithShowLabel.forEach((elem, idx) => {
            if (!elem || gridRenderedIndices.has(idx)) return;
            const { Node, ref, nodeKey } = elem;
            const field = ref?.current?.field;

            if (isHiddenVendorField(field)) return;

            if (elem?.showLabel) {
              res.push(
                <SEFieldLabel
                  label={field?.label}
                  isRequired={field?.isRequired}
                  onEdit={onSubmit && (() => {
                    field?.enable();
                    asyncRerender(() => !field?.disabled);
                  })}
                />
              );
            }
            if (field?.fieldType === 'group') {
              if (field?.extraData?.type === 'OBJECT') {
                subGroups.push(field);
              }
            } else {
              isMultiFieldPresent = isMultiFieldPresent || field?.fieldType === 'multi';
              res.push(
                <Node key={nodeKey}>
                  {field?.fieldType === 'multi' ? (
                    <SEMultiFieldGroup
                      fieldGroup={fieldGroup}
                      duplicateField={duplicateField}
                      deleteField={deleteField}
                      onSubmit={onSubmit}
                      dropdownMasterValues={dropdownMasterValues}
                      field={field}
                      showLabel={elem?.showLabel}
                      showDelete={elem?.showDelete}
                      showAdd={elem?.showAdd}
                    />
                  ) : (
                    renderFieldInputNode(field)
                  )}
                </Node>
              );
            }
          });
        } else 
              /**
               * displaying fields vertically, if any one of the field is multipleEntryType
               * */
        {
          elemsWithShowLabel.forEach((elem, idx) => {
            if (!elem) return;
            const { Node, ref, nodeKey } = elem;
            const field = ref?.current?.field;
            if (isHiddenVendorField(field)) return;
            {
              if (elem?.showLabel) {
                res.push(
                  <SEFieldLabel
                    label={field?.label}
                    isRequired={field?.isRequired}
                    onEdit={onSubmit && (() => {
                      field?.enable();
                      asyncRerender(() => !field?.disabled);
                    }
                    )}
                  />
                );
              }
              if (field?.fieldType === 'group') {
                if (field?.extraData?.type === 'OBJECT') {
                  subGroups.push(field);
                }
              } else {
                isMultiFieldPresent = isMultiFieldPresent || field?.fieldType === 'multi';
                res.push(
                  <Node key={nodeKey}>
                    {field?.fieldType === 'multi' ? (
                      <SEMultiFieldGroup
                        fieldGroup={fieldGroup}
                        duplicateField={duplicateField}
                        deleteField={deleteField}
                        onSubmit={onSubmit}
                        dropdownMasterValues={dropdownMasterValues}
                        field={field}
                        showLabel={elem?.showLabel}
                        showDelete={elem?.showDelete}
                        showAdd={elem?.showAdd}
                      />
                    ) : (
                        renderFieldInputNode(field)
                      )}
                  </Node>
                );
              }
            }
          });
        }

        // Build tab data for the Quote Selection task (SMT71): each RFQ_QUOTE field = one tab. The
        // field NODES (quoteTabNodes) must be rendered as children of SEQuoteTaskTabs so the
        // field-group render pipeline (useFieldGroup `walk`/`injectProps`) hydrates them — it only
        // traverses props.children. The task carries other field types too (totals/reason/remarks),
        // so classify each node by ITS OWN attached field meta (fieldGroup.nodes[i].renderProps.extraData;
        // `elems` is `nodes.map(...)`, so index i is an identity match, not a guess against taskFields
        // order). Only RFQ_QUOTE nodes become tabs; the rest are handled at submit (see SEAddTaskDetailModal).
        const isQuoteTask = isQuoteSelectionTaskFields(fieldGroup?.extraData?.taskFields);
        if (isQuoteTask) {
          // Collect the per-vendor tabs first, then order them by the fixed vendor sequence
          // (QUOTE_TAB_VENDOR_ORDER, ISS-293412) instead of the backend field order. `originalIndex`
          // is the stable tiebreaker: unmapped vendor fields all share the sentinel rank and must keep
          // their original relative order regardless of the engine's Array.sort stability.
          // `meta`/`node` mirror the pre-existing untyped quoteTabMeta/quoteTabNodes arrays (the field
          // render pipeline — elems, field, extraData — is `any` throughout this file), so they carry the
          // same `any` here; only the sort keys are strongly typed.
          const quoteTabs: { meta: any; node: any; order: number; originalIndex: number }[] = [];
          elems.forEach((elem, index) => {
            if (!elem) return;
            const nodeMeta = fieldGroup.nodes?.[index]?.renderProps?.extraData;
            // Per-vendor Quote Details fields become selectable tabs — RFQ_QUOTE (SMF416/417/418,
            // rendered as a quote panel) and RFQ_BID (e.g. Freight Forwarder SMF431, rendered as the
            // bid-selection UI). The Quote Candidates snapshot (SMF427) is also RFQ_QUOTE but is never
            // rendered as a tab.
            if (!isPerVendorQuoteOrBidField(nodeMeta)) return;
            const field = (elem.ref?.current as { field?: any } | undefined)?.field;
            const NodeComp = elem.Node;
            const fieldMeta = field?.extraData || nodeMeta;
            quoteTabs.push({
              meta: { key: elem.nodeKey, fieldMeta },
              node: <NodeComp key={elem.nodeKey}>{renderFieldInputNode(field)}</NodeComp>,
              order: getQuoteTabOrder(fieldMeta),
              originalIndex: index,
            });
          });
          quoteTabs
            .sort((a, b) => a.order - b.order || a.originalIndex - b.originalIndex)
            .forEach((tab) => {
              quoteTabMeta.push(tab.meta);
              quoteTabNodes.push(tab.node);
            });
        }
      }

      const renderObjectGroupLabels = () => {
        const chunkSize = 5;
        const isChunkRequired = objectGroupLabels.length > chunkSize && res.length > chunkSize;
        const chunkedObjectGroupLabels = isChunkRequired
          ? lodash.chunk(objectGroupLabels, chunkSize)
          : [objectGroupLabels];
        let highestChunkSize = 0;
        let highestResSize = 0;
        return chunkedObjectGroupLabels.map((chunk, idx) => {
          const inputRes = res.slice(idx * chunkSize, isChunkRequired ? ((idx + 1) * chunkSize) : res.length);
          highestChunkSize = Math.max(highestChunkSize, chunk.length);
          highestResSize = Math.max(highestResSize, inputRes.length);
          return (
            <React.Fragment key={idx}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${highestChunkSize}, 1fr)`,
                }}
              >
                {chunk}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${highestResSize}, 1fr)`,
                }}
              >
                {inputRes}
              </div>
            </React.Fragment>
          );
        })
      };

      /**
       * list view for multiField, and for tasks mentioned in tasksToShowInVerticalView array
       */
      const tasksToShowInVerticalView = [
        'RFQ_BID',
        'RFQ_QUOTE',
        'SAILING_SCHEDULE',
        'OBJECT',
        'TEAM_AND_USER_ALLOCATION',
        'USER_ALLOCATION',
        'ADDITIONAL_CHARGES',
      ];

      const taskType = fieldGroup.extraData?.taskFields?.[0]?.type;

      // Quote Selection task (SMT71): RFQ_QUOTE field(s) laid out as tabs with a combined footer by
      // SEQuoteTaskTabs. Detected by the PRESENCE of any RFQ_QUOTE field (the payload also carries
      // totals/reason/remarks and the field order is not guaranteed) — never by taskFields[0].
      const isQuoteSelectionTask = isQuoteSelectionTaskFields(fieldGroup.extraData?.taskFields);

      // The saved L1 deviation justification (reason + remarks), shown read-only beneath the tabs on
      // the approval view (SMT72). Empty on a fresh SMT71 task, so nothing renders there.
      const quoteFieldValueByCode = (code: string): string | undefined => {
        const field = fieldGroup.extraData?.taskFields?.find(
          (taskField) => (taskField?.masterCode || taskField?.code) === code,
        );
        const value = field?.values?.[0];
        return typeof value === 'string' ? value : undefined;
      };
      const deviationReason = isQuoteSelectionTask
        ? quoteFieldValueByCode(QUOTE_L1_DEVIATION_REASON_CODE)
        : undefined;
      const deviationRemarks = isQuoteSelectionTask
        ? quoteFieldValueByCode(QUOTE_DEVIATION_REMARKS_CODE)
        : undefined;

      const verticalView = tasksToShowInVerticalView.includes(taskType) || isQuoteSelectionTask;

      const isListView = isMultiFieldPresent || verticalView;
 
      return (
        <>
          {!!subGroups?.length && (
            <div>
              {renderSubGroups(subGroups)}
            </div>
          )}

          {isParentObject && isValidArray(objectGroupLabels) ? (
              <div>
                {renderObjectGroupLabels()}
              </div>
            ) : isQuoteSelectionTask ? (
              <SEQuoteTaskTabs
                tabMeta={quoteTabMeta}
                deviationReason={deviationReason}
                deviationRemarks={deviationRemarks}
              >
                {quoteTabNodes}
              </SEQuoteTaskTabs>
            ) : (
              <div
                style={
                  !isListView ? {
                    display: 'grid',
                    gridTemplateColumns: '1fr 2fr 1fr 2fr',
                  } : undefined}
              >
                {res}
              </div>
            )}
        </>
      );
    });
  };

  return <>{renderFieldGroup()}</>;
};

const styles = (theme) => ({});

const SEFieldGroupStyled = React.memo(withStyles(styles)(SEFieldGroup));
export default SEFieldGroupStyled;

export const SEFieldGroupWithHook = ({
  defaultNodes,
  extraData,
  duplicateField,
  deleteField,
  dropdownMasterValues,
}: {
  defaultNodes: {
    renderComponent: any; // TODO: React.ReactElement?
    renderProps: FieldHookProps;
    // formKey: string,
  }[];
  duplicateField?: (field: any) => void;
  deleteField?: (field: any) => void;
  extraData?: any;
  dropdownMasterValues?: DropdownMasterValues;
}) =>
  React.forwardRef<
    {},
    FieldNodeProps & {
      requestRerender: () => void;
    }
  >(({ children, requestRerender, ...props }, ref) => {
    const fieldGroup = useFieldGroup({
      defaultNodes,
      extraData,
      // onFirstRender: async () => form.setFieldsValue(preValues),
    });

    if (ref) {
      (ref as any).current = { field: fieldGroup };
    }

    return (
      <SEFieldGroupStyled
        fieldGroup={fieldGroup}
        duplicateField={duplicateField}
        deleteField={deleteField}
        dropdownMasterValues={dropdownMasterValues}
        // onSubmit={containersToEdit?.length ? undefined : handleAdddetail}
      />
    );
  });
